////////////////////////////////////////////
// DEEPLINK PROTOCOL — PHANTOM / SOLFLARE UNIVERSAL-LINK URL CONTRACT
////////////////////////////////////////////
//
// THE TWO WALLETS EXPOSE THE SAME v1 DEEPLINK API (connect / signMessage /
// signTransaction) DIFFERING ONLY IN HOST AND THE NAME OF THE RESPONSE'S
// ENCRYPTION-KEY PARAM. THE WALLET APP OPENS VIA UNIVERSAL LINK (WHICH ALSO
// FALLS BACK TO THE INSTALL PAGE IF THE APP IS MISSING), THE USER APPROVES,
// AND THE WALLET REDIRECTS TO redirect_link — OUR OWN URL IN THE NATIVE
// BROWSER, TAGGED ?dl=<kind> SO resume.ts KNOWS WHAT TO FINISH.
import type { SolNet } from "@/config/network";
import { tabId, type DlProvider } from "./store";

export interface DlProviderMeta {
  name: string;
  base: string;
  // RESPONSE PARAM CARRYING THE WALLET'S X25519 PUBLIC KEY ON connect
  pekParam: string;
}

export const DL_PROVIDERS: Record<DlProvider, DlProviderMeta> = {
  phantom: {
    name: "Phantom",
    base: "https://phantom.app/ul/v1",
    pekParam: "phantom_encryption_public_key",
  },
  solflare: {
    name: "Solflare",
    base: "https://solflare.com/ul/v1",
    pekParam: "solflare_encryption_public_key",
  },
};

export type DlKind = "connect" | "signin" | "tx";

// EVERY PARAM A WALLET RETURN TRIP CAN CARRY — SCRUBBED FROM THE URL ON CAPTURE
export const DL_RETURN_PARAMS = [
  "dl", "dlp", "dlt",
  "phantom_encryption_public_key", "solflare_encryption_public_key",
  "nonce", "data", "errorCode", "errorMessage",
];

// SOLANA NET -> WALLET CLUSTER PARAM
function clusterOf(net: SolNet): string {

  return net === "devnet" ? "devnet" : "mainnet-beta";
}

// REDIRECT BACK TO THIS EXACT PAGE — CURRENT PARAMS KEPT (?net/?launch SURVIVE
// THE ROUND TRIP), PRIOR RETURN PARAMS DROPPED, ?dl/?dlp/?dlt TAGGED ON
// (?dlt NAMES THE ORIGINATING TAB SO A FOREIGN RETURN TAB RESUMES ITS RECORD)
function redirectLink(kind: DlKind, provider: DlProvider): string {

  const here = new URL(location.href);
  for (const p of DL_RETURN_PARAMS) here.searchParams.delete(p);
  here.searchParams.set("dl", kind);
  here.searchParams.set("dlp", provider);
  here.searchParams.set("dlt", tabId());
  here.hash = "";
  return here.toString();
}

export function connectUrl(provider: DlProvider, net: SolNet, dappPublicKey: string): string {

  const m = DL_PROVIDERS[provider];
  return m.base + "/connect"
    + "?app_url=" + encodeURIComponent(location.origin)
    + "&dapp_encryption_public_key=" + dappPublicKey
    + "&redirect_link=" + encodeURIComponent(redirectLink("connect", provider))
    + "&cluster=" + clusterOf(net);
}

// SIGN REQUEST — ENCRYPTED PAYLOAD RIDES IN THE QUERY STRING
export function signUrl(
  provider: DlProvider,
  method: "signMessage" | "signTransaction",
  kind: DlKind,
  dappPublicKey: string,
  nonce: string,
  payload: string
): string {

  const m = DL_PROVIDERS[provider];
  return m.base + "/" + method
    + "?dapp_encryption_public_key=" + dappPublicKey
    + "&nonce=" + nonce
    + "&redirect_link=" + encodeURIComponent(redirectLink(kind, provider))
    + "&payload=" + payload;
}
